<script lang="ts">
    import { onMount } from "svelte";
    import type { FileMetadata } from "$lib/wasm/file";

    export let metadata: FileMetadata | null = null;

    let isOpen = false;
    let root: HTMLElement;

    $: if (!metadata) {
        isOpen = false;
    }

    $: title = metadata
        ? `Project: ${formatBytes(metadata.projectBytes)} (${formatExactBytes(metadata.projectBytes)})`
        : "";

    function toggle(event: MouseEvent) {
        event.stopPropagation();
        isOpen = !isOpen;
    }

    function close() {
        isOpen = false;
    }

    function closeOnOutsideClick(event: MouseEvent) {
        if (root?.contains(event.target as Node)) return;
        close();
    }

    function closeOnEscape(event: KeyboardEvent) {
        if (event.key === "Escape") {
            close();
        }
    }

    function formatBytes(bytes: number) {
        const units = ["B", "KiB", "MiB", "GiB"];
        let value = bytes;
        let unit = 0;

        while (value >= 1024 && unit < units.length - 1) {
            value /= 1024;
            unit++;
        }

        const precision = unit === 0 || value >= 100 || Number.isInteger(value) ? 0 : 1;
        return `${value.toFixed(precision)} ${units[unit]}`;
    }

    function formatExactBytes(bytes: number) {
        return `${bytes.toLocaleString()} bytes`;
    }

    function formatCount(value: number, singular: string, plural = `${singular}s`) {
        return `${value.toLocaleString()} ${value === 1 ? singular : plural}`;
    }

    onMount(() => {
        window.addEventListener("click", closeOnOutsideClick);
        window.addEventListener("keydown", closeOnEscape);

        return () => {
            window.removeEventListener("click", closeOnOutsideClick);
            window.removeEventListener("keydown", closeOnEscape);
        };
    });
</script>

<!----------------------------------------------------------------------------->

{#if metadata}
    <div class="file-info" bind:this={root}>
        <button
            class="dimensions"
            data-testid="active-file-dimensions"
            on:click={toggle}
            {title}
            aria-haspopup="true"
            aria-expanded={isOpen}
        >
            {metadata.width} x {metadata.height}
        </button>
        {#if isOpen}
            <div class="dropdown" data-testid="file-info-dropdown" tabindex="-1">
                <div class="row" data-testid="file-info-dimensions">
                    <span>Dimensions</span>
                    <span>{metadata.width} x {metadata.height} px</span>
                </div>
                <div class="row" data-testid="file-info-project-size">
                    <span>Project size</span>
                    <span>{formatBytes(metadata.projectBytes)}</span>
                </div>
                <div class="row" data-testid="file-info-exact-size">
                    <span>Exact size</span>
                    <span>{formatExactBytes(metadata.projectBytes)}</span>
                </div>
                <div class="row" data-testid="file-info-raw-pixels">
                    <span>Raw pixels</span>
                    <span>{formatBytes(metadata.rawPixelBytes)}</span>
                </div>
                <div class="row" data-testid="file-info-frames">
                    <span>Frames</span>
                    <span>{formatCount(metadata.frameCount, "frame")}</span>
                </div>
                <div class="row" data-testid="file-info-layers">
                    <span>Layers</span>
                    <span>{formatCount(metadata.layerCount, "layer")}</span>
                </div>
                <div class="row" data-testid="file-info-history">
                    <span>History</span>
                    <span>{metadata.undoCount} undo, {metadata.redoCount} redo</span>
                </div>
            </div>
        {/if}
    </div>
{/if}

<!----------------------------------------------------------------------------->

<style>
    .file-info {
        position: relative;
        display: inline-grid;
        place-items: center;
    }

    .dimensions {
        all: unset;
        cursor: pointer;
        color: var(--t400);
        white-space: nowrap;
        border-radius: 0.25em;
        padding: 0 0.25em;
    }

    .dimensions:hover,
    .dimensions:focus-visible,
    .dimensions[aria-expanded="true"] {
        color: var(--tacc);
        background: var(--g200);
    }

    .dropdown {
        position: absolute;
        top: calc(100% + 0.35em);
        right: 0;
        z-index: 100;
        min-width: 18em;
        display: grid;
        grid-template-columns: max-content minmax(0, 1fr);
        column-gap: 1.5em;
        row-gap: 0.35em;
        padding: 0.65em 0.75em;
        border: 1px solid var(--g200);
        background: var(--panel-bg, var(--g100));
        box-shadow: 0 0.35em 1.25em #0004;
        font-size: 0.875em;
    }

    .row {
        display: grid;
        grid-column: 1 / -1;
        grid-template-columns: subgrid;
        align-items: baseline;
    }

    .row span:first-child {
        color: var(--t400);
    }

    .row span:last-child {
        text-align: right;
        white-space: nowrap;
    }
</style>
